import { lazy, Suspense, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { contentLang, bcp47 } from "@/lib/locale";
import { Helmet } from "react-helmet-async";
import ContactForm from "@/components/ContactForm";
import CookieBanner from "@/components/CookieBanner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BrandMark from "@/components/BrandMark";
import CustomersCarousel from "@/components/CustomersCarousel";
import ReviewsCarousel from "@/components/ReviewsCarousel";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { LenisProvider } from "@/providers/LenisProvider";
import { GrainOverlay } from "@/components/GrainOverlay";
import { Reveal, RevealLines } from "@/components/Reveal";
import { AssemblingHeadline } from "@/components/AssemblingHeadline";
import { AmbientGlow } from "@/components/AmbientGlow";
import { ScrollProgressBar } from "@/components/ScrollProgressBar";
import { SpotlightCard } from "@/components/SpotlightCard";
import { BorderBeam } from "@/components/BorderBeam";
import { MagneticButton } from "@/components/MagneticButton";
import { CustomCursor } from "@/components/CustomCursor";
import { TextScramble } from "@/components/TextScramble";
import { VelocitySkew } from "@/components/VelocitySkew";
import { SwapLink } from "@/components/SwapLink";
import { TiltCard } from "@/components/TiltCard";
import { SignalField } from "@/components/SignalField";
// Dekoracyjna scena 3D: gdy chunk nie zdąży się pobrać, strona ma działać dalej.
const HeroScene = lazy(() =>
  import("@/components/HeroScene").catch(() => ({ default: () => null })),
);
import { CurtainStack } from "@/components/CurtainStack";
import { SectionShell, SectionBody } from "@/components/SectionShell";
import { SectionHeader } from "@/components/SectionHeader";
import { ServiceModels } from "@/components/ServiceModels";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import { KineticWordmark } from "@/components/KineticWordmark";
import { FooterEcosystem } from "@/components/FooterEcosystem";
import { LocalTime } from "@/components/LocalTime";
import mocneAiLogo from "@/assets/mocne-ai-icon.jpg";

import { useHeaderShrink } from "@/hooks/useHeaderShrink";
import { useActiveSection } from "@/hooks/useActiveSection";
import { useViewTransitionNav } from "@/hooks/useViewTransitionNav";
import { fetchJobs, type Job } from "@/lib/jobs";
import { localizeJob, categoryLabel } from "@/lib/jobs-zh";
import { openExternal } from "@/lib/external";
import { InsightsLinkIndex } from "@/components/InsightsLinkIndex";
import { organizationSchema, ORG_SAME_AS as ORG_PROFILES } from "@/content/organization";
import SideRailNav from "@/components/SideRailNav";


// Jednolite dane NAP + identyfikatory firmy używane we wszystkich blokach JSON-LD
const ORG_ID = "https://positiverecruitment.io/#organization";
const ORG_URL = "https://positiverecruitment.io/";
const ORG_NAME = "Positive Recruitment Sp. z o.o.";
const ORG_ALT_NAME = "Positive Recruitment";
const ORG_LOGO = "https://positiverecruitment.io/favicon.svg";
const ORG_IMAGE = "https://positiverecruitment.io/images/positive-recruitment-command-room.jpg";
const ORG_EMAIL = "office@positiverecruitment.io";
const ORG_PHONE = "+48724440557";
const ORG_ADDRESS = {
  "@type": "PostalAddress",
  "streetAddress": "ul. Juliusza Lea 100/21",
  "postalCode": "30-058",
  "addressLocality": "Kraków",
  "addressRegion": "Małopolskie",
  "addressCountry": "PL",
} as const;
const ORG_GEO = { "@type": "GeoCoordinates", "latitude": 50.0716, "longitude": 19.9156 } as const;
const ORG_AREA_SERVED = ["PL", "Europa Środkowo-Wschodnia"];
const ORG_SAME_AS = ORG_PROFILES;
const ORG_REFERENCE = {
  "@type": "Organization",
  "@id": ORG_ID,
  "name": ORG_NAME,
  "url": ORG_URL,
} as const;



const TECHS = [
  "Cloud",
  "Big Data",
  "AI",
  "Machine Learning",
  "Deep Learning",
  "IoT",
  "IIoT",
  "Leadership",
];

const NAV_ITEMS = [
  { key: "advantage", href: "#advantage" },
  { key: "services", href: "#services" },
  { key: "sprint", href: "#sprint" },
  { key: "principles", href: "#principles" },
  { key: "reviews", href: "#reviews" },
  { key: "careers", href: "#careers" },
  { key: "contact", href: "#contact" },
] as const;

// Stabilna referencja dla useActiveSection (nie tworzyć tablicy w renderze)
const SECTION_IDS = NAV_ITEMS.map((n) => n.key);

type SprintStep = { name: string; promise: string; detail: string; output: string };
type TitleBody = { title: string; body: string };
const HEAD_META = {
  pl: {
    title: "Agencja rekrutacji IT — Polska i CEE | Positive Recruitment",
    description:
      "Pomagamy firmom zatrudniać specjalistów IT w Polsce i CEE. Direct search, RPO i Fit-to-Win Sprint: krótka lista w 14 dni. Ponad 150 zatrudnień.",
  },
  en: {
    title: "IT Recruitment Agency in Poland & CEE | Positive Recruitment",
    description:
      "We help companies hire IT talent in Poland and CEE. Direct search, RPO and Fit-to-Win Sprint: shortlist in 14 days. Over 150 successful placements.",
  },
  zh: {
    title: "波兰及中东欧 IT 招聘机构 | Positive Recruitment",
    description:
      "我们帮助企业在波兰和中东欧招聘 IT 人才。猎头直招、RPO 与 Fit-to-Win Sprint：14 天内提供候选人短名单，已成功入职 150 多人。",
  },
} as const;

export default function PositiveRecruitment() {
  const { t, i18n } = useTranslation();
  const headLang = contentLang(i18n.resolvedLanguage || i18n.language);
  const head = HEAD_META[headLang];
  const STEPS = t("sprint.steps", { returnObjects: true }) as SprintStep[];
  const ADVANTAGE_ITEMS = t("advantage.items", { returnObjects: true }) as TitleBody[];
  const PRINCIPLES = t("principles.items", { returnObjects: true }) as TitleBody[];

  const [activeStep, setActiveStep] = useState(0);
  const [pointer, setPointer] = useState({ x: 30, y: 40 });
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobQuery, setJobQuery] = useState("");
  const [jobCategory, setJobCategory] = useState<string>("All");
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [jobsExpanded, setJobsExpanded] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [railExpanded, setRailExpanded] = useState(false);

  const reducedMotion = usePrefersReducedMotion();
  const headerShrink = useHeaderShrink(40);
  const activeSection = useActiveSection(SECTION_IDS);
  const navigateWithTransition = useViewTransitionNav();
  const heroBgRef = useRef<HTMLDivElement>(null);
  const heroBlob1Ref = useRef<HTMLDivElement>(null);
  const heroBlob2Ref = useRef<HTMLDivElement>(null);
  const heroContentRef = useRef<HTMLDivElement>(null);
  const sprintRef = useRef<HTMLElement>(null);
  const sprintFillRef = useRef<HTMLDivElement>(null);
  const sprintNodeRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Parallax hero (transform-only, przez wrappery - animacje warstw zostają nietknięte)
  // + linia postępu Sprintu rysowana wraz ze scrollem. Wszystko sterowane refami
  // (bez setState) i wygaszane przy prefers-reduced-motion.
  useEffect(() => {
    if (reducedMotion) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const y = window.scrollY;
      const vh = window.innerHeight;

      if (y < vh * 1.3) {
        if (heroBgRef.current) heroBgRef.current.style.transform = `translate3d(0, ${y * 0.18}px, 0)`;
        if (heroBlob1Ref.current) heroBlob1Ref.current.style.transform = `translate3d(0, ${y * -0.12}px, 0)`;
        if (heroBlob2Ref.current) heroBlob2Ref.current.style.transform = `translate3d(0, ${y * 0.1}px, 0)`;
        if (heroContentRef.current) {
          heroContentRef.current.style.transform = `translate3d(0, ${y * -0.05}px, 0)`;
          heroContentRef.current.style.opacity = String(Math.max(0, 1 - y / (vh * 0.85)));
        }
      }

      const el = sprintRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const p = Math.max(0, Math.min(1, (vh - rect.top) / (rect.height + vh)));
        if (sprintFillRef.current) sprintFillRef.current.style.width = `${p * 100}%`;
        const last = sprintNodeRefs.current.length - 1;
        sprintNodeRefs.current.forEach((node, i) => {
          if (!node) return;
          const active = last <= 0 ? p > 0 : p >= i / last - 0.02;
          node.style.color = active ? "#BEF264" : "rgba(255,255,255,0.3)";
          node.style.borderColor = active ? "#BEF264" : "rgba(255,255,255,0.2)";
        });
      }
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [reducedMotion]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    setJobsExpanded(false);
  }, [jobQuery, jobCategory]);


  useEffect(() => {
    let alive = true;
    fetchJobs()
      .then((data) => {
        if (alive) setJobs(data);
      })
      .finally(() => {
        if (alive) setJobsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  // Deep link: /?job=<slug> (QR codes, LinkedIn outreach) opens the role directly.
  useEffect(() => {
    if (!jobs.length) return;
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get("job");
    if (!wanted) return;
    const match = jobs.find((j) => (j.slug ?? j.url) === wanted);
    if (!match) return;
    setJobsExpanded(true);
    setExpandedJob(match.slug ?? match.url);
    requestAnimationFrame(() => {
      document.getElementById("careers")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [jobs]);

  const jobCategories = useMemo(() => {
    const set = new Set<string>();
    jobs.forEach((j) => set.add(j.category));
    return ["All", ...Array.from(set).sort()];
  }, [jobs]);

  const localizedJobs = useMemo(
    () => jobs.map((j) => localizeJob(j, headLang)),
    [jobs, headLang],
  );

  const filteredJobs = useMemo(() => {
    const q = jobQuery.trim().toLowerCase();
    return localizedJobs.filter((j) => {
      const matchCat = jobCategory === "All" || j.category === jobCategory;
      if (!matchCat) return false;
      if (!q) return true;
      const haystack = `${j.title} ${j.location} ${j.skills.join(" ")} ${j.category}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [localizedJobs, jobQuery, jobCategory]);

  const JOBS_PREVIEW_COUNT = 2;
  const visibleJobs = jobsExpanded ? filteredJobs : filteredJobs.slice(0, JOBS_PREVIEW_COUNT);
  const canToggleJobs = filteredJobs.length > JOBS_PREVIEW_COUNT;

  const previewJob = useMemo(() => {
    const job = jobs.find((j) => (j.slug ?? j.url) === expandedJob);
    return job ? localizeJob(job, headLang) : null;
  }, [jobs, expandedJob, headLang]);

  const onHeroMove = (e: PointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPointer({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  const heroSpotlightStyle: CSSProperties = {
    ["--spot-x" as never]: `${pointer.x}%`,
    ["--spot-y" as never]: `${pointer.y}%`,
    background:
      "radial-gradient(420px circle at var(--spot-x) var(--spot-y), rgba(189,255,0,0.28), transparent 60%)",
    mixBlendMode: "screen",
  };

  const step = STEPS[activeStep];

  return (
    <LenisProvider>
    <GrainOverlay opacity={0.035} />
    <CustomCursor />
    <ScrollProgressBar />
    <div className="min-h-screen bg-[#050706] text-[#FAFAF9] overflow-x-hidden lg:pl-[62px]">
      <Helmet>
        <html lang={bcp47(headLang)} />
        <title>{head.title}</title>
        <meta name="description" content={head.description} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="last-modified" content="2026-08-25" />
        <link rel="canonical" href="https://positiverecruitment.io/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://positiverecruitment.io/" />
        <meta property="og:title" content={head.title} />
        <meta property="og:description" content={head.description} />
        <meta property="og:image" content="https://positiverecruitment.io/og-cover.png" />
        <meta property="og:image:secure_url" content="https://positiverecruitment.io/og-cover.png" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
         <meta property="og:image:height" content="630" />
         <meta property="og:image:alt" content="Positive Recruitment - Agile IT Recruitment across Poland and CEE. Less noise, more signal." />
         <meta name="twitter:card" content="summary_large_image" />
         <meta name="twitter:title" content={head.title} />
         <meta name="twitter:description" content={head.description} />
         <meta name="twitter:image" content="https://positiverecruitment.io/og-cover.png" />
         <meta name="twitter:image:alt" content="Positive Recruitment - Agile IT Recruitment across Poland and CEE. Less noise, more signal." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          ...organizationSchema(),
          "@type": ["Organization", "LocalBusiness"],
          "@id": ORG_ID,
          "name": ORG_NAME,
          "alternateName": ORG_ALT_NAME,
          "url": ORG_URL,
          "logo": ORG_LOGO,
          "image": ORG_IMAGE,
          "email": ORG_EMAIL,
          "telephone": ORG_PHONE,
          "description": "Specjalistyczna agencja rekrutacji IT z Krakowa. Poszukiwania bezpośrednie i sprinty rekrutacyjne dla Cloud, Big Data, AI/ML, IoT i liderów technicznych w Polsce i CEE.",
          "foundingDate": "2022",
          "priceRange": "$$",
          "address": ORG_ADDRESS,
          "geo": ORG_GEO,
          "areaServed": ORG_AREA_SERVED,
          "sameAs": ORG_SAME_AS,
          "knowsAbout": [
            "IT recruitment",
            "Cloud Computing recruitment",
            "Big Data recruitment",
            "Artificial Intelligence recruitment",
            "Machine Learning recruitment",
            "IoT recruitment",
            "Engineering Leadership hiring",
            "Executive search",
            "Direct search",
            "CEE talent market",
            "Poland IT market",
          ],
          "knowsLanguage": [
            { "@type": "Language", "name": "Polish", "alternateName": "pl" },
            { "@type": "Language", "name": "English", "alternateName": "en" },
          ],
          "numberOfEmployees": { "@type": "QuantitativeValue", "value": 2 },
          "hasCredential": {
            "@type": "EducationalOccupationalCredential",
            "credentialCategory": "licence",
            "name": "KRAZ Employment Agency Licence",
            "identifier": "27177",
            "recognizedBy": {
              "@type": "GovernmentOrganization",
              "name": "Krajowy Rejestr Agencji Zatrudnienia (KRAZ)",
              "url": "https://kraz.praca.gov.pl/",
            },
          },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "@id": "https://positiverecruitment.io/#service",
          "name": "Positive Recruitment - Usługi rekrutacji IT",
          "description": "Specjalistyczne usługi rekrutacyjne dla branży IT: poszukiwanie bezpośrednie, rekrutacja stała i tymczasowa, executive search, mapowanie talentów oraz budowanie pipeline'ów kandydatów.",
          "url": ORG_URL,
          "image": ORG_IMAGE,
          "provider": {
            ...ORG_REFERENCE,
            "address": ORG_ADDRESS,
            "email": ORG_EMAIL,
            "telephone": ORG_PHONE,
          },
          "areaServed": ORG_AREA_SERVED,
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Rekrutacja IT",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Efektywna rekrutacja IT (stała)",
                  "description": "Kompleksowy proces rekrutacji dla stanowisk IT - od briefu po onboarding kandydata, ze średnim czasem dostarczenia 14 dni.",
                  "provider": ORG_REFERENCE,
                },
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "IT Executive Search",
                  "description": "Dedykowane poszukiwanie kadry zarządczej i liderów technicznych metodą direct search z mapowaniem rynku.",
                  "provider": ORG_REFERENCE,
                },
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Rekrutacja tymczasowa IT",
                  "description": "Szybkie dostarczenie specjalistów IT na kontrakty tymczasowe, projekty i okresy przejściowe.",
                  "provider": ORG_REFERENCE,
                },
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Mapowanie talentów i pipeline",
                  "description": "Budowa długoterminowej bazy pretendentów na kluczowe stanowiska techniczne.",
                  "provider": ORG_REFERENCE,
                },
              },
            ],
          },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AggregateRating",
          "itemReviewed": ORG_REFERENCE,
          "ratingValue": "4.9",
          "reviewCount": 10,
          "bestRating": "5",
          "worstRating": "1",
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Krzysztof Mętel" },
              "reviewBody": "Wojciech is one of the best recruiters I have met, concrete and cheerful. His qualities are sincerity, professionalism and a bit of humor. Talking with him is a real pleasure, he always finds time to talk even after working hours. I sincerely recommend.",
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "itemReviewed": ORG_REFERENCE,
            },
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Jakub Szczepek" },
              "reviewBody": "Interview with Wojtek is not standard, but after that you know, You want to have only interviews performed in that way. Skill of getting information and create relation between members of conversation is very high. Although I met Wojtek first time, after a few minutes I felt like I was talking to a good friend.",
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "itemReviewed": ORG_REFERENCE,
            },
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Keith Wingate" },
              "reviewBody": "I was tasked with staffing an operations team in Europe to help complete a 24x7 follow-the-sun support calendar. Adrian found wonderful talent and really helped me engage with them. He assisted in everything from salary negotiations to advice on labor law and conventions in the EU.",
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "itemReviewed": ORG_REFERENCE,
            },
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Ehsan Golesorkhi" },
              "reviewBody": "Wojciech has enough knowledge about IT domain to understand what exactly you are looking for. He properly supports through the recruitment process. He is friendly and professional which made me feel comfortable during the recruitment process.",
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "itemReviewed": ORG_REFERENCE,
            },
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Yana Lymar" },
              "reviewBody": "Wojciech Kluk is a motivated, forward-thinking with lots of knowledge in his field. He is a detail oriented, goal oriented, ambitious and strong co-worker. His knowledge is vast and extensive. Thanks to interpersonal skills he has great relations with both company clients and potential customers.",
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "itemReviewed": ORG_REFERENCE,
            },
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Rafał Świerczewski" },
              "reviewBody": "He was very professional towards my person, always very easy going and friendly. Each time he got new information I was being told everything within few minutes. He even reached me on holidays because he received some information that was important. Because of his devotion towards details I finally found a team and company that I'll be happy to work with for many years to come.",
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "itemReviewed": ORG_REFERENCE,
            },
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Justyna Zolna" },
              "reviewBody": "Wojtek was my career advisor. I highly appreciate our cooperation. His professional insight materialised in achieving my professional objectives. Wojtek is a trustful advisor. His easygoing and people oriented approach made it a real pleasure.",
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "itemReviewed": ORG_REFERENCE,
            },
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Mirosław Sosna" },
              "reviewBody": "A positively non-standard and very open approach makes it seem as if I have known Wojtek for a long time. Good technical knowledge helps him determine the level of the candidate well, which makes it easier to go through the next stages of recruitment.",
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "itemReviewed": ORG_REFERENCE,
            },
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Joanna Marek" },
              "reviewBody": "Wojciech definitely made it very easy and gave me a boost of energy and motivation to start a new job. He has a fresh and surprising view on how to work with the client, and is definitely focusing on finding the perfect match for both sides - employees and the company.",
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "itemReviewed": ORG_REFERENCE,
            },
            {
              "@type": "Review",
              "author": { "@type": "Person", "name": "Wojtek Pantera" },
              "reviewBody": "Adrian is a solutions oriented problem solver with a positive mindset. His ability to diagnose a situation, recommend a course of action and get things done has been extremely helpful. He is curious and thorough in seeking to understand his clients' needs and uses his deep understanding of the market to meet and exceed expectations.",
              "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" },
              "itemReviewed": ORG_REFERENCE,
            },
          ],
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Czym zajmuje się Positive Recruitment?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Positive Recruitment Sp. z o.o. to specjalistyczna agencja rekrutacji IT z Krakowa, założona w 2022 roku. Specjalizujemy się w obsadzaniu stanowisk technicznych (Cloud, Big Data, AI/ML, IoT, Engineering Leadership) poprzez bezpośrednie poszukiwania wśród pasywnych kandydatów w Polsce i Europie Środkowo-Wschodniej (CEE). Posiadamy licencję KRAZ nr 27177.",
              },
            },
            {
              "@type": "Question",
              "name": "How long does the IT recruitment process take at Positive Recruitment?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The average time-to-fill is 14 days from brief to accepted shortlist. Positive Recruitment uses the Fit-to-Win Sprint methodology - a 5-step process (Sense, Map, Engage, Validate, Hire) with direct access to passive candidates, which enables significantly faster delivery than traditional agency approaches.",
              },
            },
            {
              "@type": "Question",
              "name": "Jakie stanowiska IT rekrutuje Positive Recruitment?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Positive Recruitment specjalizuje się w rekrutacji specjalistów IT z obszarów: Cloud Computing (AWS, Azure, GCP), Big Data i Data Engineering, Sztuczna inteligencja (AI) i Machine Learning, IoT i IIoT, przywództwo techniczne (CTO, VP of Engineering, Engineering Manager). Obsługujemy rekrutację stałą, tymczasową i executive search.",
              },
            },
            {
              "@type": "Question",
              "name": "W jakich krajach działa Positive Recruitment?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Positive Recruitment obsługuje klientów przede wszystkim w Polsce (siedziba w Krakowie) oraz w regionie Europy Środkowo-Wschodniej (CEE): Czechy, Słowacja, Rumunia, Węgry, Ukraina. Wiele rekrutowanych ról jest w trybie zdalnym (remote-first).",
              },
            },
            {
              "@type": "Question",
              "name": "What is the Fit-to-Win Sprint methodology?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The Fit-to-Win Sprint is Positive Recruitment's proprietary 5-module methodology: 1) Sense - deep role and culture analysis; 2) Map - direct search talent mapping across Poland and CEE; 3) Engage - personalised outreach to passive candidates; 4) Validate - technical and soft-skill verification with references; 5) Hire - offer management and onboarding support. Each module delivers a named artefact to the client.",
              },
            },
            {
              "@type": "Question",
              "name": "Czy Positive Recruitment posiada licencję agencji zatrudnienia?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Tak. Positive Recruitment Sp. z o.o. posiada licencję Krajowego Rejestru Agencji Zatrudnienia (KRAZ) nr 27177, co potwierdza legalność działalności pośrednictwa pracy zgodnie z polskim prawem.",
              },
            },
            {
              "@type": "Question",
              "name": "Jak skontaktować się z Positive Recruitment?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Kontakt z Positive Recruitment: e-mail office@positiverecruitment.io, telefon +48 724 440 557, formularz kontaktowy na positiverecruitment.io. Biuro: ul. Juliusza Lea 100/21, 30-058 Kraków. Odpowiadamy w ciągu 24 godzin.",
              },
            },
            {
              "@type": "Question",
              "name": "What do clients and candidates say about Positive Recruitment?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Positive Recruitment holds a 4.9/5 aggregate rating from 10 verified LinkedIn recommendations. Clients praise the speed (14-day average time-to-fill), direct access to passive candidates, technical domain knowledge and transparent communication. Candidates highlight the non-standard, relationship-first interview approach and personalised career guidance.",
              },
            },
          ],
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Person",
              "@id": "https://positiverecruitment.io/o-nas/wojciech-kluk#person",
              "name": "Wojciech Kluk",
              "jobTitle": "CEO and Founder",
              "worksFor": { "@type": "Organization", "@id": ORG_ID },
              "knowsAbout": [
                "IT recruitment",
                "Direct search",
                "Cloud Computing",
                "Big Data",
                "AI/ML recruitment",
                "IoT",
                "CEE talent market",
                "Poland IT market",
              ],
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Kraków",
                "addressCountry": "PL",
              },
            },
            {
              "@type": "Person",
              "@id": "https://positiverecruitment.io/#person-adrian",
              "name": "Adrian",
              "jobTitle": "Co-Founder & Senior IT Recruitment Consultant",
              "worksFor": { "@type": "Organization", "@id": ORG_ID },
              "knowsAbout": [
                "IT recruitment",
                "EU labour law",
                "Direct search",
                "CEE talent market",
                "Cross-cultural talent engagement",
                "Engineering team building",
              ],
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Kraków",
                "addressCountry": "PL",
              },
            },
          ],
        })}</script>

      </Helmet>
      {/* HERO */}
      <section
        onPointerMove={onHeroMove}
        className="relative min-h-screen w-full overflow-hidden bg-[#050706] pr-hero-vignette flex flex-col"
        style={{ minHeight: "100svh" }}
      >
        {/* Generatywne tło: szum (siatka punktów) → sygnał (linia). Zastępuje zdjęcie zespołu. */}
        <SignalField className="absolute inset-0 opacity-70" />

        {/* Warstwa 3D (spline-like): bryły reagujące na kursor, tylko dekoracyjne */}
        {!reducedMotion && (
          <Suspense fallback={null}>
            <HeroScene className="absolute inset-y-0 right-0 w-full md:w-[62%] lg:w-[58%] pointer-events-none opacity-60 md:opacity-90 [mask-image:radial-gradient(80%_75%_at_60%_50%,#000_45%,transparent_100%)]" />
          </Suspense>
        )}

        {/* Animated cinematic light sweep */}
        <div
          className="absolute inset-0 pointer-events-none anim-hero-sweep"
          style={{
            background:
              "linear-gradient(105deg, transparent 30%, rgba(189,255,0,0.06) 45%, rgba(255,255,255,0.04) 50%, rgba(189,255,0,0.06) 55%, transparent 70%)",
          }}
          aria-hidden
        />

        {/* Fluid lime blobs for depth (parallax wrappers) */}
        <div ref={heroBlob1Ref} className="absolute inset-0 pointer-events-none" style={{ willChange: "transform" }} aria-hidden>
          <div
            className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] pointer-events-none anim-hero-blob-1 anim-hero-pulse blur-[40px] md:blur-[80px]"
            style={{
              background:
                "radial-gradient(circle at center, rgba(189,255,0,0.14), transparent 60%)",
            }}
            aria-hidden
          />
        </div>
        <div ref={heroBlob2Ref} className="absolute inset-0 pointer-events-none" style={{ willChange: "transform" }} aria-hidden>
          <div
            className="absolute -bottom-[10%] -left-[10%] w-[60%] h-[60%] pointer-events-none anim-hero-blob-2 blur-[50px] md:blur-[100px]"
            style={{
              background:
                "radial-gradient(circle at center, rgba(189,255,0,0.10), transparent 55%)",
            }}
            aria-hidden
          />
        </div>

        <div className="absolute inset-0 pointer-events-none" style={heroSpotlightStyle} aria-hidden />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, rgba(5,7,6,0.9) 0%, rgba(5,7,6,0.5) 42%, rgba(5,7,6,0) 100%)",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,7,6,0.6) 0%, rgba(5,7,6,0) 25%, rgba(5,7,6,0) 70%, rgba(5,7,6,0.95) 100%)",
          }}
          aria-hidden
        />
        <div
          className="absolute -bottom-20 -left-20 w-[260px] h-[260px] md:-bottom-40 md:-left-40 md:w-[600px] md:h-[600px] pointer-events-none anim-hero-pulse"
          style={{
            background:
              "radial-gradient(circle, rgba(189,255,0,0.18), transparent 70%)",
          }}
          aria-hidden
        />

      {/* Header (fixed) */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b transition-all duration-300 ${
          headerShrink ? "bg-background/80 border-white/10" : "bg-background/70 border-white/5"
        }`}
      >
        <div
          className={`max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-10 lg:ml-[max(62px,calc((100vw-1218px)/2))] flex items-center justify-between gap-3 lg:gap-4 transition-all duration-300 ${
            headerShrink ? "py-2.5" : "py-3.5 md:py-4"
          }`}
        >
          <a href="#" className="flex items-center gap-2.5 lg:gap-3 group shrink-0" aria-label={t("nav.home")}>
            <BrandMark size={36} className="shrink-0 transition-transform group-hover:rotate-6 md:hidden" />
            <BrandMark size={44} className="hidden md:block shrink-0 transition-transform group-hover:rotate-6" />
            <span className="text-[11px] sm:text-sm lg:text-[13px] xl:text-[15px] font-bold uppercase tracking-[0.14em] sm:tracking-widest leading-tight whitespace-nowrap">
              <span className="block">POSITIVE</span>
              <span className="block">RECRUITMENT</span>
            </span>
          </a>

          <div className="hidden lg:block flex-1" />
          <div className="flex items-center gap-2 lg:gap-3 shrink-0">
            <LanguageSwitcher className="hidden sm:inline-flex shrink-0" />

            <button
              type="button"
              className="lg:hidden inline-flex items-center justify-center w-11 h-11 text-white hover:text-lime-300"

              aria-label={mobileNavOpen ? t("nav.closeMenu") : t("nav.openMenu")}
              onClick={() => setMobileNavOpen((v) => !v)}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
                <line x1="3" y1="7" x2="21" y2="7" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="17" x2="21" y2="17" />
              </svg>
              <span className="sr-only">{mobileNavOpen ? t("nav.closeMenu") : t("nav.openMenu")}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Boczny pasek nawigacji (desktop) */}
      <SideRailNav
        items={NAV_ITEMS.map((n) => ({ key: n.key, href: n.href, label: t(`nav.${n.key}`) }))}
        activeKey={activeSection}
        expanded={railExpanded}
        onMouseEnter={() => setRailExpanded(true)}
        onMouseLeave={() => setRailExpanded(false)}
        onNavigate={(key) => navigateWithTransition(key)}
        rpoLabel={t("nav.rpo")}
        insightsLabel={t("nav.insights")}
      />



      {/* Mobile nav overlay */}
      {mobileNavOpen && (
        <div data-lenis-prevent className="fixed inset-0 z-50 lg:hidden bg-[#050706] flex flex-col pt-6 px-4 sm:px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] overflow-y-auto overscroll-contain">
          <div className="flex items-center justify-between mb-8">
            <span className="text-sm font-bold uppercase tracking-widest text-white">{t("nav.menu")}</span>
            <button
              type="button"
              aria-label={t("nav.closeMenu")}
              onClick={() => setMobileNavOpen(false)}
              className="inline-flex items-center justify-center w-11 h-11 -mr-2 text-white hover:text-lime-300"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square">
                <line x1="5" y1="5" x2="19" y2="19" />
                <line x1="19" y1="5" x2="5" y2="19" />
              </svg>
            </button>
          </div>
          <nav className="flex flex-col gap-2">
            {NAV_ITEMS.map((n, i) => (
              <Reveal key={n.href} delayMs={i * 60}>
                <a
                  href={n.href}
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileNavOpen(false);
                    navigateWithTransition(n.key);
                  }}
                  className="block py-4 text-base uppercase tracking-[0.28em] text-white/85 hover:text-lime-300 border-b border-white/10 transition-colors"
                >
                  {t(`nav.${n.key}`)}
                </a>
              </Reveal>
            ))}
            <Reveal delayMs={NAV_ITEMS.length * 60}>
              <Link
                to="/services/it-rpo-poland"
                onClick={() => setMobileNavOpen(false)}
                className="block py-4 text-base uppercase tracking-[0.28em] text-white/85 hover:text-lime-300 border-b border-white/10 transition-colors"
              >
                {t("nav.rpo")}
              </Link>
              <Link
                to="/insights"
                onClick={() => setMobileNavOpen(false)}
                className="block py-4 text-base uppercase tracking-[0.28em] text-white/85 hover:text-lime-300 border-b border-white/10 transition-colors"
              >
                {t("nav.insights")}
              </Link>
            </Reveal>
          </nav>
          <div className="mt-8">
            <LanguageSwitcher />
          </div>
        </div>
      )}


        {/* Hero content */}
        <div
          ref={heroContentRef}
          className="relative z-10 flex-1 flex flex-col justify-center md:justify-start w-full max-w-[1280px] mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-10 md:pb-40"
        >
          <div className="max-w-4xl">
            <p className="eyebrow mb-5 md:mb-8"><TextScramble text={String(t("hero.eyebrow"))} /></p>
            <AssemblingHeadline
              className="h-mega text-[clamp(1.9rem,8.6vw,3.25rem)] sm:text-7xl md:text-8xl lg:text-[7rem] text-white break-normal"
              lines={[String(t("hero.title1")), String(t("hero.title2"))]}
              srOnly={` - ${t("hero.h1Sr")}`}
              hint={t("hero.dragHint") as string}
              replayLabel={t("hero.replay") as string}
            />


            <div className="mt-6 md:mt-14 border-l-2 border-lime-300 pl-5 md:pl-8 max-w-[56rem]">
              <h2 className="h-display text-balance text-lg sm:text-2xl md:text-[1.75rem] lg:text-[1.5rem] text-white">{t("hero.subtitle")}</h2>
              <p className="mt-3 md:mt-6 text-body text-sm md:text-lg leading-relaxed max-w-[44rem]">{t("hero.lead")}</p>
            </div>
            <div className="mt-6 md:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-5">
              <BorderBeam className="w-full sm:w-auto">
                <MagneticButton className="w-full sm:w-auto">
                  <a
                    href="#contact"
                    data-cta="hero_primary_contact"
                    className="group inline-flex items-center justify-center gap-3 bg-lime-300 text-black font-bold uppercase tracking-wider text-[11px] sm:text-sm px-5 sm:px-7 py-3.5 sm:py-5 hover:-translate-y-1 transition-transform text-center w-full sm:w-auto"
                  >
                    <span className="leading-tight">{t("hero.ctaPrimary")}</span>
                    <span className="inline-block transition-transform group-hover:translate-x-1 shrink-0">→</span>
                  </a>
                </MagneticButton>
              </BorderBeam>
              <a
                href="#sprint"
                data-cta="hero_secondary_sprint"
                className="inline-flex items-center justify-center gap-3 glass hairline text-white font-bold uppercase tracking-wider text-[11px] sm:text-sm px-5 sm:px-7 py-3.5 sm:py-5 hover:border-lime-300/60 transition-colors text-center w-full sm:w-auto"
              >
                <span className="leading-tight">{t("hero.ctaSecondary")}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CONVERSION GAP */}
      <SectionShell tone="panel" size="md">
        <SectionHeader
          index="01"
          eyebrow={String(t("conversion.eyebrow"))}
          titleLines={String(t("conversion.title")).split("\n")}
          lead={
            <>
              <Reveal delayMs={80} withBlur>
                <p>{t("conversion.p1")}</p>
              </Reveal>
              <Reveal delayMs={180} withBlur>
                <p>{t("conversion.p2")}</p>
              </Reveal>
            </>
          }
        />
      </SectionShell>


      {/* ADVANTAGE */}
      <SectionShell
        id="advantage"
        tone="dark"
        size="lg"
        backdrop={<AmbientGlow />}
        innerClassName="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-20 items-start"
      >
        <div className="lg:sticky lg:top-24 self-start">
          <SectionHeader
            index="02"
            eyebrow={String(t("advantage.eyebrow"))}
            titleLines={[String(t("advantage.title"))]}
            layout="stacked"
          />
          <Reveal delayMs={200}>
            <a
              href="#services"
              className="group relative mt-10 flex items-center justify-between gap-4 border border-white/10 bg-[#050706] px-5 py-4 transition-colors hover:border-lime-300/40"
            >
              <span className="absolute left-0 top-0 h-px w-0 bg-lime-300 transition-all duration-500 group-hover:w-full" aria-hidden />
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/60 group-hover:text-white">
                {headLang === "pl"
                  ? "Osiem modeli współpracy"
                  : headLang === "en"
                  ? "Eight engagement models"
                  : "八种合作模式"}
              </span>
              <span className="font-mono text-[10px] text-lime-300 transition-transform group-hover:translate-x-1">→</span>
            </a>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 sm:divide-x sm:divide-white/10">
          {ADVANTAGE_ITEMS.map((item, i) => (
            <Reveal
              as="div"
              delayMs={i * 80}
              key={i}
              className={`py-7 sm:py-8 sm:px-8 first:sm:pl-0 sm:[&:nth-child(3)]:pl-0 sm:[&:nth-child(3)]:border-l-0 border-b border-white/10 sm:[&:nth-child(3)]:border-b-0 sm:last:border-b-0 ${
                i > 1 ? "sm:border-t sm:border-white/10" : ""
              }`}
            >
              <TiltCard className="h-full">
                <SpotlightCard className="h-full">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-lime-300 mb-4">
                    0{i + 1}
                  </div>
                  <h3 className="text-xl font-bold uppercase tracking-tight text-white mb-3">
                    {item.title}
                  </h3>
                  <p className="text-body text-sm leading-relaxed">{item.body}</p>
                </SpotlightCard>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </SectionShell>

      <SectionShell tone="dark" size="md" className="border-t-0" innerClassName="!py-0 pb-14 md:pb-24">

          {/* Marquee */}
          <div className="overflow-hidden border-y border-white/10 py-6 pr-marquee-mask pr-marquee-pause">
            <div
              className="flex anim-marquee whitespace-nowrap gap-12 text-xs uppercase tracking-[0.4em] text-white/70"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              {[...TECHS, ...TECHS, ...TECHS, ...TECHS].map((tech, i) => (
                <span key={i} className="flex items-center gap-12">
                  {tech}
                  <span className="text-lime-300">/</span>
                </span>
              ))}
            </div>
          </div>
      </SectionShell>

      {/* TRUSTED BY */}
      <SectionShell id="customers" tone="lime" size="md">
        <SectionHeader
          index="03"
          eyebrow={String(t("customers.eyebrow"))}
          titleLines={String(t("customers.title")).split("\n")}
          titleClassName="text-4xl md:text-5xl"
          tone="light"
          lead={
            <Reveal delayMs={100} withBlur>
              <p>{t("customers.body")}</p>
            </Reveal>
          }
        />
        <SectionBody>
          <CustomersCarousel onLime />
        </SectionBody>
      </SectionShell>

      {/* SERVICE MODELS */}
      <SectionShell id="services" tone="dark" size="lg">
        <SectionHeader
          index="04"
          eyebrow={
            headLang === "pl" ? "Modele współpracy" : headLang === "en" ? "Cooperation models" : "合作模式"
          }
          titleLines={[
            headLang === "pl"
              ? "Od jednego eksperta po cały zespół"
              : headLang === "en"
              ? "From one expert to a whole team"
              : "从单个专家到整支团队",
          ]}
          lead={
            <Reveal delayMs={80} withBlur>
              <p>
                {headLang === "pl"
                  ? "Osiem modeli współpracy w rekrutacji IT. Dobieramy je do tempa, budżetu i skali zatrudnienia - nie odwrotnie."
                  : headLang === "en"
                  ? "Eight engagement models for IT recruitment. We match them to your pace, budget and hiring scale - not the other way round."
                  : "八种 IT 招聘合作模式。我们按您的节奏、预算与招聘规模来匹配，而非相反。"}
              </p>
              <dl className="mt-8 grid grid-cols-3 gap-4 border-t border-white/10 pt-5">
                {[
                  { v: "8", l: headLang === "pl" ? "modeli" : headLang === "en" ? "models" : "模式" },
                  { v: "48 h", l: headLang === "pl" ? "pierwsza lista" : headLang === "en" ? "first shortlist" : "首份名单" },
                  { v: "PL / CEE", l: headLang === "pl" ? "zasięg" : headLang === "en" ? "coverage" : "覆盖" },
                ].map((s) => (
                  <div key={s.l}>
                    <dt className="sr-only">{s.l}</dt>
                    <dd className="font-mono text-xl md:text-2xl text-lime-300">{s.v}</dd>
                    <dd className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-white/45">{s.l}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          }
        />
        <SectionBody>
          <ServiceModels lang={headLang} />
        </SectionBody>
      </SectionShell>




      {/* SPRINT + PRINCIPLES - kurtynowe przejście (Zasady nasuwają się na Sprint) */}
      <CurtainStack>
      <CurtainStack.Base>
      <SectionShell
        id="sprint"
        sectionRef={sprintRef}
        tone="panel"
        size="lg"
        className="border-b border-white/10"
        innerClassName="grid lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-20"
      >
        <div className="lg:sticky lg:top-12 self-start">

            <SectionHeader
              index="05"
              eyebrow={String(t("sprint.eyebrow"))}
              titleLines={[String(t("sprint.title"))]}
              layout="stacked"
              lead={
                <>
                  <p>{t("sprint.lead")}</p>
                  <p className="mt-4 text-white/70">{t("sprint.lead2")}</p>
                </>
              }
            />


            {/* Linia postępu 01→05 - rysuje się w miarę scrollowania sekcji */}
            <div className="mt-10 hidden sm:block max-w-md" aria-hidden>
              <div className="relative">
                <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-white/15" />
                <div
                  ref={sprintFillRef}
                  className="absolute left-0 top-1/2 -translate-y-1/2 h-px bg-lime-300"
                  style={{ width: "0%", boxShadow: "0 0 10px rgba(189,255,0,0.7)" }}
                />
                <div className="relative flex justify-between">
                  {STEPS.map((s, i) => (
                    <div
                      key={s.name}
                      ref={(el) => {
                        sprintNodeRefs.current[i] = el;
                      }}
                      className="font-mono text-[11px] flex items-center justify-center w-9 h-9 border bg-[#050706] transition-colors duration-500"
                      style={{ color: "rgba(255,255,255,0.3)", borderColor: "rgba(255,255,255,0.2)" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Panel */}
          <div
            className="relative glass border border-lime-300/40 p-6 md:p-8 overflow-hidden"
            style={{ boxShadow: "0 0 80px -20px rgba(189,255,0,0.25)" }}
          >
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
              <div className="absolute top-0 left-0 h-px w-1/2 bg-gradient-to-r from-transparent via-lime-300 to-transparent anim-edge-glint" />
            </div>

            {/* Step chips */}
            <div className="flex flex-wrap gap-2 mb-8 relative">
              {STEPS.map((s, i) => (
                <button
                  key={s.name}
                  onClick={() => setActiveStep(i)}
                  className={`font-mono text-[10px] uppercase tracking-[0.28em] px-3 py-2 border transition-colors ${
                    activeStep === i
                      ? "bg-lime-300 text-black border-lime-300"
                      : "bg-white/5 text-white/70 border-white/15 hover:border-lime-300/60"
                  }`}
                  aria-pressed={activeStep === i}
                >
                  {i + 1}. {s.name}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Radar */}
              <div
                className="relative aspect-square w-full max-w-[260px] sm:max-w-sm mx-auto"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(189,255,0,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(189,255,0,0.08) 1px, transparent 1px)",
                  backgroundSize: "28px 28px",
                }}
              >
                <div className="absolute inset-0 border border-white/15" />
                {[0.35, 0.65, 0.95].map((r, i) => (
                  <div
                    key={i}
                    className="absolute border border-white/15 rounded-full"
                    style={{
                      width: `${r * 100}%`,
                      height: `${r * 100}%`,
                      left: `${(1 - r) * 50}%`,
                      top: `${(1 - r) * 50}%`,
                    }}
                  />
                ))}
                <div className="absolute inset-0 anim-radar-turn pointer-events-none">
                  {/* Ślad sweepa - zanikający wycinek za kreską */}
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{ background: "conic-gradient(from 0deg, rgba(189,255,0,0.16), transparent 42deg)" }}
                  />
                  <div
                    className="absolute left-1/2 top-1/2 origin-left h-px"
                    style={{
                      width: "50%",
                      background: "linear-gradient(90deg, rgba(189,255,0,0.9), transparent)",
                      boxShadow: "0 0 12px rgba(189,255,0,0.7)",
                    }}
                  />
                </div>
                {STEPS.map((s, i) => {
                  const angle = (i / STEPS.length) * Math.PI * 2 - Math.PI / 2;
                  const r = 0.42;
                  const x = 50 + Math.cos(angle) * r * 100;
                  const y = 50 + Math.sin(angle) * r * 100;
                  const isActive = activeStep === i;
                  return (
                    <button
                      key={s.name}
                      onClick={() => setActiveStep(i)}
                      aria-label={t("sprint.activateStep", { name: `${i + 1}. ${s.name}` })}
                      className={`absolute -translate-x-1/2 -translate-y-1/2 transition-all ${
                        isActive ? "" : "opacity-60 hover:opacity-100"
                      }`}
                      style={{
                        left: `${x}%`,
                        top: `${y}%`,
                        transform: `translate(-50%,-50%) scale(${isActive ? 1.25 : 1})`,
                      }}
                    >
                      {isActive && (
                        <span
                          aria-hidden
                          className="anim-pr-radar-pulse pointer-events-none absolute left-1/2 top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2 border border-lime-300"
                        />
                      )}
                      <span
                        className={`font-mono text-[10px] flex items-center justify-center w-7 h-7 border ${
                          isActive
                            ? "bg-lime-300 text-black border-lime-300"
                            : "bg-black/60 text-white/80 border-white/30 hover:border-lime-300"
                        }`}
                        style={isActive ? { boxShadow: "0 0 18px rgba(189,255,0,0.8)" } : {}}
                      >
                        {i + 1}
                      </span>
                    </button>
                  );
                })}
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-mono text-[9px] uppercase tracking-[0.2em] text-lime-200 bg-black/70 border border-lime-300/40 px-2 py-1 text-center">
                  {t("sprint.hiringSignal")}
                </div>
              </div>

              {/* Detail */}
              <div className="relative overflow-hidden border border-white/10 bg-black/30 p-5 flex flex-col">
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div className="absolute inset-x-0 h-12 bg-gradient-to-b from-lime-300/10 to-transparent anim-scan" />
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-lime-200 mb-3">
                  {t("sprint.activeSprint")}
                </div>
                <h3 className="text-lg md:text-xl font-bold uppercase tracking-tight text-white mb-3">
                  {step.name}: {step.promise}
                </h3>
                <p className="text-body text-sm leading-relaxed mb-5">{step.detail}</p>
                <div className="border-t border-white/10 pt-4 mt-auto">
                  <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/80 mb-1">
                    {t("sprint.clientReceives")}
                  </div>
                  <div className="text-lime-300 font-bold text-base">{step.output}</div>
                </div>
                <div className="mt-5 h-1 bg-white/10 relative">
                  <div
                    className="absolute inset-y-0 left-0 bg-lime-300 transition-all duration-500"
                    style={{ width: `${((activeStep + 1) / STEPS.length) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
      </SectionShell>
      </CurtainStack.Base>

      {/* PRINCIPLES */}
      <CurtainStack.Over>
      <SectionShell
        id="principles"
        tone="lime"
        size="lg"
        innerClassName="grid md:grid-cols-2 gap-10 md:gap-20"
      >
          <SectionHeader
            index="06"
            eyebrow={String(t("principles.eyebrow"))}
            titleLines={[String(t("principles.title"))]}
            tone="light"
            layout="stacked"
          />

          <div className="grid sm:grid-cols-2 gap-x-10">
            {PRINCIPLES.map((p, i) => (
              <Reveal as="div" delayMs={i * 80} key={i} className="border-t border-black/30 last:border-b-0 py-5">
                <h3 className="text-lg font-black uppercase tracking-tight mb-1">{p.title}</h3>
                <p className="text-sm text-black/75 leading-relaxed">{p.body}</p>
              </Reveal>
            ))}
          </div>
      </SectionShell>
      </CurtainStack.Over>
      </CurtainStack>

      {/* REVIEWS */}
      <SectionShell id="reviews" tone="dark" size="lg">
        <SectionHeader
          index="07"
          eyebrow={String(t("reviews.eyebrow"))}
          titleLines={String(t("reviews.title")).split("\n")}
          lead={
            <Reveal delayMs={100} withBlur className="space-y-4">
              <p>{t("reviews.body1")}</p>
              <p className="text-white/85">{t("reviews.body2")}</p>
            </Reveal>
          }
        />
        <SectionBody>
          <ReviewsCarousel />
        </SectionBody>
      </SectionShell>

      {/* CAREERS */}
      <SectionShell id="careers" tone="lime-soft" size="lg">
          <SectionHeader
            index="08"
            eyebrow={String(t("careers.eyebrow"))}
            titleLines={String(t("careers.title")).split("\n")}
            className="mb-10 md:mb-16"
            lead={
              <Reveal delayMs={100} withBlur>
                <p>{t("careers.body")}</p>
              </Reveal>
            }
          />


          {/* Filter bar */}
          <div className="glass hairline p-4 md:p-5 flex flex-col md:flex-row gap-4 md:items-center md:justify-between mb-8">
            <div className="flex flex-wrap gap-2">
              {jobCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setJobCategory(cat)}
                  className={`font-mono text-[10px] uppercase tracking-[0.28em] px-3 py-2 border transition-colors ${
                    jobCategory === cat
                      ? "bg-lime-300 text-black border-lime-300"
                      : "bg-white/5 text-white/70 border-white/15 hover:border-lime-300/60"
                  }`}
                  aria-pressed={jobCategory === cat}
                >
                  {cat === "All" ? t("careers.all") : categoryLabel(cat, headLang)}
                </button>
              ))}
            </div>
            <label className="md:w-72 w-full">
              <span className="sr-only">{t("careers.searchLabel")}</span>
              <input
                type="search"
                value={jobQuery}
                onChange={(e) => setJobQuery(e.target.value)}
                placeholder={t("careers.searchPlaceholder")}
                className="w-full bg-black/40 border border-white/15 px-4 py-3 text-white text-sm focus:border-lime-300 outline-none placeholder:text-white/70"
              />
            </label>
          </div>

          {/* Jobs grid */}
          {jobsLoading ? (
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/80 py-12">
              {t("careers.loading")}
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/80 py-12">
              {t("careers.empty")}
            </div>
          ) : (
            <>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visibleJobs.map((job, i) => {
                const slug = job.slug ?? job.url;
                return (
                <Reveal
                  as="li"
                  key={slug}
                  delayMs={(i % 8) * 60}
                  className={`h-full p-6 md:p-7 flex flex-col group relative overflow-hidden border border-white/10 ${
                    job.hot ? "bg-[#0f140a]" : "bg-[#080b09]"
                  }`}
                >
                  <div
                    className={`pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent ${
                      job.hot ? "via-lime-300/70" : "via-lime-300/0"
                    } to-transparent group-hover:via-lime-300/60 transition-colors`}
                  />
                  {/* OfferRow: sygnaturowy pasek 3px wjeżdżający od lewej na hover */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute left-0 top-0 h-full w-[3px] -translate-x-full bg-lime-300 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-0"
                  />
                  {job.hot && (
                    <span className="pr-badge-accent absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.28em] px-2 py-1">
                      {t("careers.hot")}
                    </span>
                  )}
                  <div className="flex items-center justify-between mb-4 gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-lime-200">
                      {String(i + 1).padStart(2, "0")} · {categoryLabel(job.category, headLang)}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/75">
                      {job.workmode}
                    </span>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold uppercase tracking-tight text-white mb-3 leading-tight">
                    {job.title}
                  </h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/60 mb-4">
                    <span className={`font-mono tracking-[0.18em] text-lime-200 ${/remote/i.test(job.location) ? "uppercase" : ""}`}>
                      {job.location}
                    </span>
                    <span aria-hidden>·</span>
                    <span>{job.salary}</span>
                  </div>
                  <p className="text-body text-sm leading-relaxed mb-5 line-clamp-3">
                    {job.shortDescription}
                  </p>
                  {job.skills.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {job.skills.slice(0, 5).map((s) => (
                        <span
                          key={s}
                          className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-white/15 text-white/70"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => setExpandedJob(slug)}
                    className="mt-auto inline-flex items-center justify-between gap-3 border border-lime-300/50 text-lime-200 font-bold uppercase tracking-widest text-[11px] px-4 py-3 hover:bg-lime-300 hover:text-black transition-colors"
                    aria-label={t("careers.viewDetailsAria", { title: job.title })}
                  >
                    {t("careers.viewDetails")}
                    <span aria-hidden className="transition-transform group-hover:translate-x-1">→</span>
                  </button>
                </Reveal>
                );
              })}
            </ul>

            {/* Job preview modal */}
            <Dialog open={!!previewJob} onOpenChange={(open) => !open && setExpandedJob(null)}>
              {previewJob && (
                <DialogContent className="bg-[#080b09] border border-white/10 text-[#FAFAF9] max-w-3xl max-h-[90vh] overflow-y-auto p-0 gap-0">
                  <div className="relative p-6 md:p-8 border-b border-white/10">
                    {previewJob.hot && (
                      <span className="pr-badge-accent absolute top-4 right-4 font-mono text-[10px] uppercase tracking-[0.28em] px-2 py-1">
                        {t("careers.hot")}
                      </span>
                    )}
                    <DialogHeader>
                      <DialogDescription className="font-mono text-[10px] uppercase tracking-[0.28em] text-lime-200 mb-3">
                        {categoryLabel(previewJob.category, headLang)} · {previewJob.workmode}
                      </DialogDescription>
                      <DialogTitle className="text-xl md:text-2xl font-bold uppercase tracking-tight text-white leading-tight">
                        {previewJob.title}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/60 mt-4">
                      <span className={`font-mono tracking-[0.18em] text-lime-200 ${/remote/i.test(previewJob.location) ? "uppercase" : ""}`}>
                        {previewJob.location}
                      </span>
                      <span aria-hidden>·</span>
                      <span>{previewJob.salary}</span>
                    </div>
                  </div>

                  <div className="p-6 md:p-8">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-8">
                      {[
                        [t("careers.modal.location"), previewJob.location],
                        [t("careers.modal.workmode"), previewJob.workmode],
                        [t("careers.modal.compensation"), previewJob.salary],
                      ].map(([k, v]) => (
                        <div key={k} className="bg-[#080b09] p-4">
                          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/80 mb-1">{k}</div>
                          <div className="text-sm text-white">{v}</div>
                        </div>
                      ))}
                    </div>

                    {previewJob.skills.length > 0 && (
                      <div className="mb-8">
                        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/80 mb-3">{t("careers.modal.stack")}</div>
                        <div className="flex flex-wrap gap-2">
                          {previewJob.skills.map((s) => (
                            <span key={s} className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 border border-white/15 text-white/80">{s}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-x-10">
                      {([
                        [t("careers.modal.about"), previewJob.responsibilities],
                        [t("careers.modal.bring"), previewJob.requirements],
                        [t("careers.modal.nice"), previewJob.niceToHave],
                        [t("careers.modal.offer"), previewJob.benefits],
                      ] as const).map(([heading, items]) => (
                        <div key={heading} className="mb-8">
                          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-lime-200 mb-3">{heading}</div>
                          <ul className="space-y-2">
                            {items.map((item) => (
                              <li key={item} className="flex gap-3 text-body text-sm leading-relaxed">
                                <span aria-hidden className="text-lime-300 mt-1">›</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    <p className="text-white/85 text-xs md:text-sm mb-6 leading-relaxed">{t("careers.modal.footer")}</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a href="#contact" data-cta="job_apply" onClick={() => setExpandedJob(null)} className="flex-1 inline-flex items-center justify-center gap-3 bg-lime-300 text-black font-bold uppercase tracking-widest text-[11px] px-5 py-4 hover:-translate-y-1 transition-transform text-center">
                        {t("careers.modal.apply")}<span aria-hidden>→</span>
                      </a>
                      <button type="button" onClick={() => setExpandedJob(null)} className="inline-flex items-center justify-center gap-3 glass hairline text-white font-bold uppercase tracking-widest text-[11px] px-5 py-4 hover:border-lime-300/60 transition-colors text-center">
                        {t("careers.modal.back")}
                      </button>
                    </div>
                  </div>
                </DialogContent>
              )}
            </Dialog>
            </>
          )}

          {!jobsLoading && canToggleJobs && (
            <div className="mt-10 flex flex-col items-center gap-3">
              <button
                type="button"
                onClick={() => setJobsExpanded((v) => !v)}
                aria-expanded={jobsExpanded}
                aria-controls="careers"
                className="inline-flex items-center gap-3 glass hairline text-white font-bold uppercase tracking-widest text-[11px] px-7 py-4 hover:border-lime-300/60 hover:text-lime-200 transition-colors text-center"
              >
                {jobsExpanded
                  ? t("careers.showLess")
                  : t("careers.showAll", { count: filteredJobs.length })}
                <span
                  aria-hidden
                  className={`inline-block transition-transform ${jobsExpanded ? "rotate-180" : ""}`}
                >
                  ↓
                </span>
              </button>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/75">
                {t("careers.showingOf", {
                  shown: jobsExpanded ? filteredJobs.length : visibleJobs.length,
                  total: filteredJobs.length,
                })}
              </p>
            </div>
          )}
      </SectionShell>


      {/* CONTACT - limonkowy blok (echo sekcji Zasad, punkt kulminacyjny konwersji) */}
      <SectionShell
        id="contact"
        tone="lime"
        size="lg"
        innerClassName="grid md:grid-cols-2 gap-10 md:gap-20 md:items-start"
      >
          <div className="md:sticky md:top-28 self-start">
            <SectionHeader
              index="09"
              eyebrow={String(t("contact.eyebrow"))}
              titleLines={[String(t("contact.title"))]}
              titleClassName="text-4xl md:text-7xl"
              tone="light"
              layout="stacked"
              lead={<p>{t("contact.lead")}</p>}
            />
            <div className="mt-6 md:mt-10 pt-5 md:pt-6 border-t border-black/20 flex flex-col gap-3">
              <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-black">
                <span className="h-1.5 w-1.5 bg-black" aria-hidden />
                {t("form.replyTime")}
              </span>
              <a
                href="mailto:office@positiverecruitment.io"
                className="w-fit font-mono text-[12px] tracking-[0.12em] text-black/70 hover:text-black transition-colors"
              >
                office@positiverecruitment.io
              </a>
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-black/50">
                Kraków · Poland &amp; CEE
              </span>
            </div>
          </div>
          <ContactForm />
      </SectionShell>

      <InsightsLinkIndex />

      {/* Footer */}

      <footer className="relative overflow-hidden border-t border-lime-300/25 bg-[#050706]">
        <div className="relative z-10 max-w-[1280px] mx-auto px-5 md:px-10 pt-10 md:pt-20 pb-10 md:pb-14">
          <KineticWordmark text="POSITIVE RECRUITMENT" size="sm" />

          <div className="mt-8 md:mt-14">
            <FooterEcosystem
              eyebrow={t("footer.ecosystem.eyebrow")}
              intro={t("footer.ecosystem.intro")}
              items={[
                { index: "01", name: "MOCNE.AI", href: "https://mocne.ai", tagline: t("footer.ecosystem.mocne") },
                { index: "02", name: "P-REC", href: "https://p-rec.pl", tagline: t("footer.ecosystem.prec") },
                { index: "03", name: "POSITIVE RECRUITMENT MED", href: "https://positiverecruitment.pl", tagline: t("footer.ecosystem.prec_med") },
              ]}
            />
          </div>

          <div className="mt-10 md:mt-14 grid gap-x-8 gap-y-8 md:gap-y-10 md:grid-cols-12">
            {/* Brand + legal */}
            <div className="md:col-span-5 lg:col-span-4 flex flex-col gap-3 border-t border-white/10 pt-4 md:pt-5">
              <div className="flex items-center gap-3 md:gap-4">
                <BrandMark size={28} />
                <span className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.18em] md:tracking-[0.28em] text-white/60 break-words">
                  {t("footer.address")}
                </span>
              </div>
              <span className="inline-flex items-center gap-2 font-mono text-[9px] md:text-[10px] uppercase tracking-[0.18em] md:tracking-[0.28em] text-white/70">
                <span className="inline-block h-1.5 w-1.5 bg-lime-300" aria-hidden />
                {t("footer.kraz")}
              </span>
              <span className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.18em] md:tracking-[0.28em] text-white/50">
                {t("footer.founded")}
              </span>
            </div>

            {/* Nav - two short columns instead of one long list */}
            <nav
              className="md:col-span-4 lg:col-span-5 grid grid-cols-2 gap-x-6 border-t border-white/10 pt-4 md:pt-5"
              aria-label="Footer"
            >
              {[
                {
                  title: t("footer.colCompany"),
                  links: [
                    { to: "/uslugi", label: t("footer.services") },
                    { to: "/services/it-rpo-poland", label: t("footer.linkRpo") },
                    { to: "/dla-kandydatow", label: t("footer.candidates") },
                    { to: "/o-nas", label: t("footer.about") },
                    { to: "/kontakt", label: t("footer.contact") },
                  ],
                },
                {
                  title: t("footer.colInsights"),
                  links: [
                    { to: "/insights", label: t("footer.insights").replace(/\s*→\s*$/, "") },
                    { to: "/insights/it-salary-guide-poland", label: t("footer.linkSalary") },
                    { to: "/insights/rekrutacja-it-krakow", label: t("footer.linkKrakow") },
                    { to: "/insights/rekrutacja-it-warszawa", label: t("footer.linkWarszawa") },
                  ],
                },
              ].map((col) => (
                <div key={col.title} className="flex flex-col gap-1.5 md:gap-3">
                  <span className="font-mono text-[9px] uppercase tracking-[0.28em] text-lime-300/70 mb-1">{col.title}</span>
                  {col.links.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      className="block py-1.5 md:py-0 font-mono text-[10px] uppercase tracking-[0.2em] md:tracking-[0.24em] text-white/60 hover:text-lime-300 transition-colors"
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              ))}
            </nav>

            {/* Meta */}
            <div className="md:col-span-3 flex flex-row items-center justify-between gap-4 border-t border-white/10 pt-4 md:pt-5 md:flex-col md:items-end">
              <LocalTime />
              <a
                href="https://mocne.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 border border-white/10 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.28em] text-white/40 hover:text-lime-300 hover:border-lime-300/40 transition-colors"
              >
                <img
                  src={mocneAiLogo}
                  alt="Mocne.ai logo"
                  className="h-6 w-auto object-contain"
                />
                {t("footer.copyright")}
              </a>
            </div>
          </div>

          {/* Bottom bar: legal + social */}
          <div className="mt-8 md:mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-4 font-mono text-[9px] uppercase tracking-[0.24em] text-white/40">
            <Link to="/privacy-policy" className="hover:text-lime-300 transition-colors">
              {t("footer.policy").replace(/\s*→\s*$/, "")}
            </Link>
            <a
              href="https://www.linkedin.com/company/positive-recruitment/"
              onClick={(e) => openExternal(e, "https://www.linkedin.com/company/positive-recruitment/")}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-lime-300 transition-colors"
            >
              LinkedIn ↗
            </a>
          </div>
        </div>


      </footer>
      <CookieBanner />
    </div>
    </LenisProvider>
  );
}
